import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { CircleHelp, Split, SquarePlus, Zap } from 'lucide-react'
import { cn } from '@ludus/ui'
import {
  NODE_BRANCHES,
  isKnownNodeType,
  nodeRole,
  type FlowNode,
  type NodeBranch,
  type NodeRole,
} from '@/features/flows/types.ts'
import { CARD_WIDTH, clampCell } from './grid.ts'
import { handlesOf, overviewOf, useFlowEditorState } from './useFlowEditorState.ts'
import { nodeTypeKey, type NodeLabels } from './useNodeLabels.ts'

/**
 * One node as a card: what it is, what its own spec says it will do, and the
 * two exits it can leave by.
 *
 * The body is `NODE_SPECS[type].overview(options)` and nothing else (D9). The
 * canvas therefore renders a node type it has never heard of correctly, because
 * the rows come from the type's own spec — the reason `overview` exists at all
 * rather than a `switch` in this file.
 *
 * **Every pointer gesture has a keyboard equivalent, and they are the same
 * code.** The card is focusable and focusing it selects (so the canvas's Delete
 * acts on it); the arrow keys move it one grid cell, which is what a drag does;
 * each handle is a real button, so Enter arms a connection and Enter on a
 * target completes it — the same `connect` the mouse reaches through xyflow's
 * `onConnect`. `Delete` on a source handle clears that branch, which is the
 * keyboard's answer to selecting an arrow and deleting it.
 */

export interface FlowNodeData extends Record<string, unknown> {
  node: FlowNode
  /** The trigger cannot reach it: legal, never runs, drawn subdued (D9). */
  unreachable: boolean
  /** How many of the save's refusals name this node. */
  issues: number
  labels: NodeLabels
  /**
   * Whether any node's `resolve`/`reject` points here — including where the
   * role rules say nothing may. The entrance is drawn for such a node even
   * though `handlesOf` withholds it, because an edge whose handle is missing is
   * dropped by xyflow: the operator would see the validator's red ring and have
   * no arrow to select and no way to remove the pointer.
   */
  targeted: boolean
  /** This node is a step of the run currently open in the runs panel (#176). */
  onRunPath: boolean
}

export type FlowCanvasNode = Node<FlowNodeData, 'flow'>

const ROLE_ICON: Record<NodeRole, typeof Zap> = {
  trigger: Zap,
  condition: Split,
  action: SquarePlus,
}

/**
 * The role of a stored type, or `null` for one this build has no spec for.
 *
 * Every lookup below goes through it. Indexing the two role maps with a raw
 * `node.type` returned `undefined` for an unknown node, and an `undefined`
 * icon makes React throw "Element type is invalid" — taking down the whole flow
 * page over a single node, which is exactly what the API layer's refusal to
 * narrow `type` is meant to avoid.
 */
const roleOf = (type: string): NodeRole | null => (isKnownNodeType(type) ? nodeRole(type) : null)

const ROLE_TONE: Record<NodeRole, string> = {
  trigger: 'border-primary/50 bg-primary-light',
  condition: 'border-toast-warning-text/40 bg-toast-warning/40',
  action: 'border-accent/40 bg-toast-success/40',
}

/** Drawn plainly, so an unrecognised node reads as "unknown", not as an action. */
const UNKNOWN_TONE = 'border-border bg-background-secondary'

/** How far the arrow keys move a focused node: one cell, the same unit a drag snaps to. */
const ARROW_STEP: Record<string, { dx: number; dy: number }> = {
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
}

export function FlowNodeCard({ data, selected }: NodeProps<FlowCanvasNode>) {
  const { t } = useTranslation()
  const { node, unreachable, issues, labels, targeted, onRunPath } = data
  const role = roleOf(node.type)
  const Icon = role === null ? CircleHelp : ROLE_ICON[role]
  const offered = handlesOf(node.type)
  // The handles the rules offer, plus any this node's own stored pointers need.
  // Same reason as `targeted`: a branch an action must not have is still drawn
  // while it holds a target, so the edge renders and can be deleted.
  const strayBranches = NODE_BRANCHES.filter(
    (branch) => node[branch] !== null && !offered.sources.includes(branch),
  )
  const handles = {
    target: offered.target || targeted,
    sources: [...offered.sources, ...strayBranches],
  }

  const select = useFlowEditorState((s) => s.select)
  const moveNode = useFlowEditorState((s) => s.moveNode)
  const arm = useFlowEditorState((s) => s.arm)
  const disarm = useFlowEditorState((s) => s.disarm)
  const connect = useFlowEditorState((s) => s.connect)
  const disconnect = useFlowEditorState((s) => s.disconnect)
  const pending = useFlowEditorState((s) => s.pending)

  const rows = overviewOf(node)

  function onCardKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      disarm()
      return
    }
    const step = ARROW_STEP[event.key]
    if (step === undefined) return
    // Both, and for different reasons: `preventDefault` stops the pane from
    // scrolling under the node, `stopPropagation` stops xyflow's own arrow
    // handling from adding a few pixels on top of the cell this moves by.
    event.preventDefault()
    event.stopPropagation()
    moveNode(node.id, clampCell({ x: node.position.x + step.dx, y: node.position.y + step.dy }))
  }

  const branchLabel = (branch: NodeBranch): string =>
    branch === 'resolve'
      ? t('pages.flows.canvas.branch.resolve', { defaultValue: 'Then' })
      : t('pages.flows.canvas.branch.reject', { defaultValue: 'Otherwise' })

  return (
    <div
      tabIndex={0}
      role="group"
      aria-label={t('pages.flows.canvas.node.label', {
        key: node.key,
        type: t(`pages.flows.canvas.type.${nodeTypeKey(node.type)}`, { defaultValue: node.type }),
        defaultValue: '{{type}} node {{key}}',
      })}
      onFocus={() => select(node.id)}
      onKeyDown={onCardKeyDown}
      style={{ width: CARD_WIDTH }}
      className={cn(
        'relative flex flex-col rounded-xl border bg-background-secondary text-left shadow-sm outline-none transition-opacity',
        role === null ? UNKNOWN_TONE : ROLE_TONE[role],
        selected && 'ring-2 ring-ring',
        // The run's path, drawn under the selection ring rather than instead of
        // it: "what ran" and "what I am editing" are different questions and a
        // person often has both open at once.
        onRunPath && !selected && 'ring-2 ring-primary',
        issues > 0 && 'border-toast-error-text/70',
        // Legal, and never run: subdued rather than hidden or refused, so work
        // in progress survives a save (D9, `unreachableNodeIds`).
        unreachable && 'opacity-50',
      )}
    >
      {handles.target && (
        <Handle
          id="in"
          type="target"
          position={Position.Left}
          tabIndex={0}
          role="button"
          aria-label={t('pages.flows.canvas.handle.in', {
            key: node.key,
            defaultValue: 'Connect into {{key}}',
          })}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            if (pending === null) return
            connect(pending.source, pending.branch, node.id)
          }}
          className={cn(
            '!h-3 !w-3 !border-2 !border-border !bg-background',
            pending !== null && '!border-primary !bg-primary',
          )}
        />
      )}

      <header className="flex items-center gap-2 border-b border-border/50 px-3 py-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/80" aria-hidden="true" />
        <span className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/80">
          {t(`pages.flows.canvas.type.${nodeTypeKey(node.type)}`, { defaultValue: node.type })}
        </span>
        <span className="ml-auto truncate font-mono text-[10px] text-muted">{node.key}</span>
      </header>

      <dl className="flex flex-col gap-1 px-3 py-2">
        {rows.length === 0 && (
          <p className="text-[11px] italic text-muted">
            {t('pages.flows.canvas.node.unconfigured', { defaultValue: 'Not configured yet' })}
          </p>
        )}
        {rows.map((row) => (
          <div key={row.label} className="flex min-w-0 items-baseline gap-2">
            <dt className="shrink-0 text-[10px] uppercase tracking-wide text-muted">
              {t(`pages.flows.canvas.option.${row.label}`, { defaultValue: row.label })}
            </dt>
            <dd className="min-w-0 flex-1 truncate text-right text-[11px] text-foreground">
              {labels.text(node, row)}
            </dd>
          </div>
        ))}
      </dl>

      {handles.sources.length > 0 && (
        <footer className="flex flex-col gap-1 border-t border-border/50 px-3 py-1.5">
          {handles.sources.map((branch) => {
            const armed = pending?.source === node.id && pending.branch === branch
            return (
              <div key={branch} className="relative flex items-center justify-end">
                <span
                  className={cn(
                    'text-[10px] uppercase tracking-wide',
                    armed ? 'text-primary' : 'text-muted',
                  )}
                >
                  {branchLabel(branch)}
                </span>
                <Handle
                  id={branch}
                  type="source"
                  position={Position.Right}
                  tabIndex={0}
                  role="button"
                  aria-label={t('pages.flows.canvas.handle.out', {
                    key: node.key,
                    branch: branchLabel(branch),
                    defaultValue: 'Connect {{key}} · {{branch}}',
                  })}
                  onKeyDown={(event) => {
                    if (event.key === 'Delete' || event.key === 'Backspace') {
                      event.preventDefault()
                      event.stopPropagation()
                      disconnect(node.id, branch)
                      return
                    }
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    event.stopPropagation()
                    arm(node.id, branch)
                  }}
                  className={cn(
                    '!h-3 !w-3 !border-2',
                    branch === 'resolve'
                      ? '!border-accent !bg-background'
                      : '!border-muted !bg-background',
                    armed && '!border-primary !bg-primary',
                  )}
                  style={{ top: 'auto', bottom: 'auto', right: -18 }}
                />
              </div>
            )
          })}
        </footer>
      )}
    </div>
  )
}
